import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, ArrowLeft, Building2, Inbox } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { formatEUR, statusColor } from "@/lib/format";
import OperationForm from "@/components/admin/OperationForm";

const emptyForm = {
  title: "", location: "", status: "In corso", type: "",
  value: "", roi: "", mq: "", description: "", image_url: "",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [operations, setOperations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmId, setConfirmId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [ops, cts] = await Promise.all([api.get("/operations"), api.get("/contacts")]);
    setOperations(ops.data);
    setContacts(cts.data);
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setUser(r.data); load().catch(() => {}); })
      .catch(() => { localStorage.removeItem("cs_token"); navigate("/admin"); });
  }, [load, navigate]);

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (op) => { setEditing(op); setShowForm(true); };

  const handleSaved = async () => {
    setShowForm(false);
    await load();
  };

  const remove = async (id) => {
    try {
      await api.delete(`/operations/${id}`);
      toast.success("Operazione eliminata.");
      setConfirmId(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = () => { localStorage.removeItem("cs_token"); navigate("/admin"); };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (!user) {
    return (
      <main className="min-h-screen bg-[#0A0A0C] flex items-center justify-center">
        <p className="text-zinc-500 text-sm tracking-[0.2em] uppercase">Verifica accesso...</p>
      </main>
    );
  }

  const totalValue = operations.reduce((s, o) => s + (Number(o.value) || 0), 0);
  const byStatus = (s) => operations.filter((o) => o.status === s).length;

  return (
    <main className="min-h-screen bg-[#0A0A0C] text-zinc-100 grain-overlay" data-testid="admin-dashboard-container">
      <Seo title="Area Riservata — Capital Stone S.r.l." description="Area riservata Capital Stone S.r.l." path="/admin/dashboard" noindex />
      <header className="border-b border-amber-500/10 bg-[#0D0D10]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="px-4 sm:px-8 max-w-7xl mx-auto flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Capital Stone" className="h-9 w-auto" />
            <span className="text-zinc-500 text-[10px] tracking-[0.3em] uppercase">Gestionale</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-back-site-link" className="hidden sm:flex items-center gap-2 text-xs text-zinc-500 hover:text-[#D4AF37] transition-colors duration-300">
              <ArrowLeft size={13} /> Sito
            </Link>
            <Link to="/admin/lead-trilocale" data-testid="admin-leads-link" className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#D4AF37] border border-amber-500/40 px-3 py-2 hover:bg-amber-500/10 transition-colors duration-300">
              Lead Trilocale
            </Link>
            <span className="hidden sm:block text-xs text-zinc-600">{user.email}</span>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-zinc-400 border border-white/10 px-3 py-2 hover:border-red-400/50 hover:text-red-400 transition-colors duration-300"
            >
              <LogOut size={13} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="px-4 sm:px-8 max-w-7xl mx-auto py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Operazioni totali", value: operations.length },
            { label: "Valore complessivo", value: formatEUR(totalValue) },
            { label: "In corso", value: byStatus("In corso") },
            { label: "Concluse", value: byStatus("Conclusa") },
          ].map((s) => (
            <div key={s.label} className="border border-amber-500/15 bg-[#121216] p-5">
              <p className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{s.label}</p>
              <p className="font-display text-2xl sm:text-3xl text-zinc-100 mt-2">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-12 mb-6">
          <h2 className="flex items-center gap-3 font-display text-2xl font-semibold">
            <Building2 size={20} className="text-[#D4AF37]" /> Operazioni
          </h2>
          <button
            data-testid="admin-add-operation-btn"
            onClick={openNew}
            className="flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0C] text-xs tracking-[0.15em] uppercase font-semibold px-5 py-3 hover:bg-[#F3E5AB] transition-colors duration-300"
          >
            <Plus size={14} /> Nuova Operazione
          </button>
        </div>

        <div className="border border-white/10 overflow-x-auto" data-testid="admin-operation-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-zinc-500 border-b border-white/10 bg-[#121216]">
                <th className="px-5 py-4">Titolo</th>
                <th className="px-5 py-4">Location</th>
                <th className="px-5 py-4">Stato</th>
                <th className="px-5 py-4">Valore</th>
                <th className="px-5 py-4">ROI</th>
                <th className="px-5 py-4 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {operations.map((op) => (
                <tr key={op.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors duration-200">
                  <td className="px-5 py-4 font-medium text-zinc-200">{op.title}</td>
                  <td className="px-5 py-4 text-zinc-500">{op.location}</td>
                  <td className="px-5 py-4">
                    <span className={`text-[10px] tracking-[0.15em] uppercase font-mono border px-2.5 py-1 ${statusColor(op.status)}`}>
                      {op.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-gold">{formatEUR(op.value)}</td>
                  <td className="px-5 py-4 text-zinc-400">{op.roi}</td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        data-testid="admin-edit-operation-btn"
                        onClick={() => openEdit(op)}
                        className="w-8 h-8 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-[#D4AF37] hover:border-amber-500/50 transition-colors duration-300"
                      >
                        <Pencil size={13} />
                      </button>
                      {confirmId === op.id ? (
                        <button
                          data-testid="admin-confirm-delete-btn"
                          onClick={() => remove(op.id)}
                          className="h-8 px-3 text-[10px] tracking-[0.1em] uppercase border border-red-400/50 text-red-400 hover:bg-red-400/10 transition-colors duration-300"
                        >
                          Conferma
                        </button>
                      ) : (
                        <button
                          data-testid="admin-delete-operation-btn"
                          onClick={() => setConfirmId(op.id)}
                          className="w-8 h-8 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-400/50 transition-colors duration-300"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {operations.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-600">Nessuna operazione presente.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <h2 className="flex items-center gap-3 font-display text-2xl font-semibold mt-14 mb-6">
          <Inbox size={20} className="text-[#D4AF37]" /> Richieste ricevute
        </h2>
        <div className="space-y-3" data-testid="admin-contacts-list">
          {contacts.length === 0 && <p className="text-zinc-600 text-sm">Nessuna richiesta ricevuta finora.</p>}
          {contacts.map((c) => (
            <div key={c.id} className="border border-white/10 bg-[#121216] p-5" data-testid="admin-contact-item">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="text-zinc-100 font-medium">{c.name}</span>
                <span className="text-gold text-xs">{c.email}</span>
                {c.phone && <span className="text-zinc-500 text-xs">{c.phone}</span>}
                <span className="text-[10px] tracking-[0.15em] uppercase font-mono border border-amber-500/25 text-amber-400/90 px-2 py-0.5">
                  {c.request_type}
                </span>
                <span className="text-zinc-600 text-xs ml-auto">
                  {c.created_at ? new Date(c.created_at).toLocaleString("it-IT") : ""}
                </span>
              </div>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{c.message}</p>
            </div>
          ))}
        </div>
      </div>

      <OperationForm
        open={showForm}
        operation={editing}
        onClose={() => setShowForm(false)}
        onSaved={handleSaved}
      />
    </main>
  );
}
